import React from 'react';
import {
  Inspect as InspectIcon,
  Film,
  Users,
  Database,
  Cpu,
  HardDrive,
  Activity,
  ArrowLeft,
  RefreshCw,
  Clock,
  Coins
} from 'lucide-react';

export type InspectViewTab = 'clips' | 'users' | 'tokenomics' | 'database' | 'daemon' | 'storage' | 'diagnostics';

interface InspectNavProps {
  activeTab: InspectViewTab;
  onTabChange: (tab: InspectViewTab) => void;
  onRefresh: () => void;
  isLoading: boolean;
  lastUpdated: Date | null;
  onBackToApp?: () => void;
  unbakedCount?: number;
  orphanedCount?: number;
}

export const InspectNav: React.FC<InspectNavProps> = ({
  activeTab,
  onTabChange,
  onRefresh,
  isLoading,
  lastUpdated,
  onBackToApp,
  unbakedCount = 0,
  orphanedCount = 0
}) => {
  const tabs: Array<{
    id: InspectViewTab;
    label: string;
    icon: React.ReactNode;
    badge?: number | null;
    badgeColor?: string;
  }> = [
    {
      id: 'clips',
      label: 'Clips Diagnostics',
      icon: <Film size={16} />,
      badge: orphanedCount > 0 ? orphanedCount : null,
      badgeColor: 'var(--accent-amber)'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: <Users size={16} />
    },
    {
      id: 'tokenomics',
      label: 'Tokenomics & Ledger',
      icon: <Coins size={16} />
    },
    {
      id: 'database',
      label: 'Database Explorer',
      icon: <Database size={16} />
    },
    {
      id: 'daemon',
      label: 'Daemon Monitor',
      icon: <Cpu size={16} />,
      badge: unbakedCount > 0 ? unbakedCount : null,
      badgeColor: 'var(--accent-coral)'
    },
    {
      id: 'storage',
      label: 'Storage & KV',
      icon: <HardDrive size={16} />
    },
    {
      id: 'diagnostics',
      label: 'Diagnostics',
      icon: <Activity size={16} />
    }
  ];

  return (
    <div style={{
      marginBottom: '2rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '1.25rem'
    }}>
      {/* Top Header Row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          {onBackToApp && (
            <button
              type="button"
              onClick={onBackToApp}
              title="Return to SoulSync Studio"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 14px',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-glass)',
                background: 'var(--bg-glass)',
                color: 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: 600,
                transition: 'all var(--transition-fast)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#fff';
                e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.25)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = 'var(--text-secondary)';
                e.currentTarget.style.borderColor = 'var(--border-glass)';
              }}
            >
              <ArrowLeft size={16} />
              <span>Back to Studio</span>
            </button>
          )}

          <div style={{
            padding: '10px',
            borderRadius: 'var(--radius-md)',
            background: 'linear-gradient(135deg, var(--accent-purple) 0%, var(--accent-coral) 100%)',
            boxShadow: '0 4px 16px rgba(139, 92, 246, 0.35)'
          }}>
            <InspectIcon size={22} color="white" />
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 style={{
                fontSize: '1.65rem',
                fontWeight: 800,
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-display)',
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}>
                SoulSync Inspector
              </h1>
              <span style={{
                padding: '2px 8px',
                borderRadius: 'var(--radius-full)',
                background: 'rgba(139, 92, 246, 0.15)',
                border: '1px solid var(--border-glow)',
                color: 'var(--accent-purple)',
                fontSize: '0.72rem',
                fontFamily: 'var(--font-mono)',
                fontWeight: 700
              }}>
                ADMIN v2.2
              </span>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
              Cloudflare D1, R2, KV & Modal GPU Pipeline Diagnostics
            </p>
          </div>
        </div>

        {/* Right side: Last sync & Manual Refresh */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {lastUpdated && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.78rem',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-mono)'
            }}>
              <Clock size={13} />
              <span>Synced {lastUpdated.toLocaleTimeString()}</span>
            </div>
          )}

          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '9px 18px',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-glass)',
              background: 'var(--bg-glass)',
              color: 'var(--text-primary)',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-body)',
              fontWeight: 600,
              fontSize: '0.85rem',
              opacity: isLoading ? 0.6 : 1,
              transition: 'all var(--transition-fast)'
            }}
          >
            <RefreshCw size={15} className={isLoading ? 'spin' : ''} />
            <span>{isLoading ? 'Syncing...' : 'Refresh'}</span>
          </button>
        </div>
      </div>

      {/* Navigation Tabs Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)',
        overflowX: 'auto',
        scrollbarWidth: 'none'
      }}>
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '9px 16px',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: isActive
                  ? 'linear-gradient(135deg, rgba(139, 92, 246, 0.25) 0%, rgba(255, 107, 107, 0.2) 100%)'
                  : 'transparent',
                color: isActive ? '#ffffff' : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: isActive ? 700 : 500,
                fontFamily: 'var(--font-body)',
                whiteSpace: 'nowrap',
                transition: 'all var(--transition-fast)',
                borderBottom: isActive ? '2px solid var(--accent-purple)' : '2px solid transparent'
              }}
            >
              <span style={{ color: isActive ? 'var(--accent-purple)' : 'inherit' }}>
                {tab.icon}
              </span>
              <span>{tab.label}</span>
              {tab.badge !== null && tab.badge !== undefined && tab.badge > 0 && (
                <span style={{
                  padding: '1px 6px',
                  borderRadius: 'var(--radius-full)',
                  background: tab.badgeColor ? `${tab.badgeColor}25` : 'rgba(255, 255, 255, 0.1)',
                  color: tab.badgeColor || '#fff',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  marginLeft: '2px'
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
